import { useState, type ReactElement, type ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface RailTooltipProps {
  /** Show the hint. Off for controls whose label is already on screen. */
  enabled: boolean;
  content: ReactNode;
  /** Open state of a menu this control triggers, when it triggers one. */
  menuOpen?: boolean;
  children: ReactElement;
}

/**
 * Right-side hint for the sidebar's icon-only rail. Must render inside a
 * `TooltipProvider` (the sidebar provides one).
 *
 * The Tooltip and its trigger are rendered in both sidebar states, so
 * collapsing or expanding never remounts — and never blurs — the control
 * inside. A menu returns focus to its trigger on close, which would reopen
 * the hint over the trigger, so the hint stays shut after a menu closes until
 * the pointer comes back or focus leaves.
 *
 * Gating only decides whether the content is drawn; Radix keeps its own open
 * state. With hoverable content (Radix's default) leaving the trigger does
 * not close a tooltip — its content does, once the pointer leaves the grace
 * area — so a hint opened while hidden was never closed, and every icon
 * hovered on the expanded sidebar popped up and stuck once it collapsed.
 * These hints are plain labels: leaving the trigger closes them.
 */
export function RailTooltip({
  enabled,
  content,
  menuOpen = false,
  children,
}: RailTooltipProps) {
  const [suppressed, setSuppressed] = useState(false);
  const [previousMenuOpen, setPreviousMenuOpen] = useState(menuOpen);

  if (menuOpen !== previousMenuOpen) {
    setPreviousMenuOpen(menuOpen);
    if (!menuOpen) setSuppressed(true);
  }

  const visible = enabled && !menuOpen && !suppressed;

  return (
    <Tooltip disableHoverableContent>
      <TooltipTrigger
        asChild
        onPointerEnter={() => setSuppressed(false)}
        onBlur={() => setSuppressed(false)}
      >
        {children}
      </TooltipTrigger>
      {visible && (
        <TooltipContent side="right" className="flex items-center gap-2">
          {content}
        </TooltipContent>
      )}
    </Tooltip>
  );
}
